import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import {
  CheckCircle2, XCircle, Inbox, Loader2, Plane, Coins, FileText, CalendarDays, Gift, History,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_ICONS = {
  leaves: CalendarDays, expenses: Coins, travel: Plane, letters: FileText, programs: Gift,
};

// Per-approval-type color theme — makes the queue scannable at a glance.
// If ONE section has pending items → use its color. Multiple → fall back to red (default urgency).
const SECTION_THEME = {
  leaves:   { icon: 'text-emerald-600', chip: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/40',   label: 'Leave' },
  expenses: { icon: 'text-amber-600',   chip: 'bg-amber-500/15 text-amber-700 border-amber-500/40',         label: 'Expense' },
  travel:   { icon: 'text-sky-600',     chip: 'bg-sky-500/15 text-sky-700 border-sky-500/40',               label: 'Travel' },
  letters:  { icon: 'text-violet-600',  chip: 'bg-violet-500/15 text-violet-700 border-violet-500/40',      label: 'Letter' },
  programs: { icon: 'text-rose-600',    chip: 'bg-rose-500/15 text-rose-700 border-rose-500/40',            label: 'Program' },
};

const ApprovalsPage = () => {
  const [pending, setPending] = useState(null);
  const [recent, setRecent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingOn, setActingOn] = useState(null); // {section, item}
  const [actionType, setActionType] = useState(null); // 'approve' | 'reject'
  const [remarks, setRemarks] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API_URL}/api/approvals/pending`, { withCredentials: true }),
        axios.get(`${API_URL}/api/approvals/recent`, { withCredentials: true }),
      ]);
      setPending(p.data); setRecent(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const performAction = async () => {
    if (!actingOn || !actionType) return;
    setSubmitting(true);
    try {
      const { section, item } = actingOn;
      const status = actionType === 'approve' ? 'approved' : 'rejected';
      let url;
      let body;
      let params;
      if (section.key === 'leaves') {
        // Backend has only /approve — body carries status
        url = `/api/leaves/${item.id}/approve`;
        body = { status, remarks: remarks || '' };
      } else if (section.key === 'expenses') {
        // Backend expects status as query param, no body
        url = `/api/expenses/${item.id}/approve`;
        params = { status };
        body = null;
      } else {
        // Travel / Letters / Programs — separate /approve and /reject endpoints
        url = (actionType === 'approve' ? section.approve_url : section.reject_url).replace('{id}', item.id);
        body = { remarks: remarks || '' };
      }
      await axios({
        method: 'PUT',
        url: `${API_URL}${url}`,
        params,
        data: body,
        withCredentials: true,
      });
      toast.success(`Marked ${status}`);
      setActingOn(null); setActionType(null); setRemarks('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${actionType}`);
    } finally {
      setSubmitting(false);
    }
  };

  const renderItem = (section, item) => {
    const Icon = SECTION_ICONS[section.key] || Inbox;
    let subtitle = '';
    if (section.key === 'leaves') subtitle = `${item.leave_type || ''} · ${item.start_date} → ${item.end_date} (${item.days} day${item.days === 1 ? '' : 's'})`;
    else if (section.key === 'expenses') subtitle = `₹${(item.amount || 0).toLocaleString('en-IN')} · ${item.category || ''}`;
    else if (section.key === 'travel') subtitle = `${item.travel_type || ''} · ${item.category || ''} · ${item.summary || ''}`;
    else if (section.key === 'letters') subtitle = `${item.letter_type || ''}${item.purpose ? ' · ' + item.purpose : ''}`;
    else if (section.key === 'programs') subtitle = `${item.program_title || ''} · ${item.proof_url ? 'proof attached' : 'no proof'}`;

    return (
      <div key={item.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-muted/30 transition" data-testid={`pending-${section.key}-${item.id}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 rounded-lg bg-primary/10 text-primary"><Icon className="w-4 h-4" /></div>
          <div className="min-w-0">
            <p className="text-sm font-medium">{item.employee_name || (item.employee_id || '').slice(0, 8)}{item.employee_department ? <span className="text-xs text-muted-foreground ml-2">· {item.employee_department}</span> : null}</p>
            <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
            {item.reason && <p className="text-xs italic text-muted-foreground truncate">&ldquo;{item.reason}&rdquo;</p>}
          </div>
        </div>
        <div className="flex gap-1.5 shrink-0">
          <Button size="sm" variant="outline" className="gap-1.5 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-950/30"
            onClick={() => { setActingOn({ section, item }); setActionType('reject'); setRemarks(''); }}
            data-testid={`reject-${section.key}-${item.id}`}>
            <XCircle className="w-3.5 h-3.5" /> Reject
          </Button>
          <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => { setActingOn({ section, item }); setActionType('approve'); setRemarks(''); }}
            data-testid={`approve-${section.key}-${item.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
          </Button>
        </div>
      </div>
    );
  };

  if (loading) return <div className="py-12 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Inbox className="w-6 h-6 text-primary" />
            Approvals Dashboard
          </h1>
          <p className="text-muted-foreground text-sm">
            {pending?.total_pending || 0} pending across all modules · scope: {pending?.scope === 'org' ? 'Organisation-wide' : 'My team'}
          </p>
        </div>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" className="gap-2" data-testid="tab-pending">
            <Inbox className="w-4 h-4" />
            Pending {pending?.total_pending ? <Badge variant="secondary" className="ml-1 text-xs">{pending.total_pending}</Badge> : null}
          </TabsTrigger>
          <TabsTrigger value="recent" className="gap-2" data-testid="tab-recent">
            <History className="w-4 h-4" />
            Recent (last 30 days) {recent?.total ? <Badge variant="secondary" className="ml-1 text-xs">{recent.total}</Badge> : null}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4 space-y-3">
          {/* Per-type summary chips — colored so HR can see the mix instantly */}
          {(pending?.sections || []).some(s => (s.items || []).length > 0) && (
            <div className="flex flex-wrap items-center gap-2" data-testid="approvals-summary">
              {(pending?.sections || []).map((section) => {
                const count = section.items.length;
                if (count === 0) return null;
                const theme = SECTION_THEME[section.key] || SECTION_THEME.leaves;
                const Icon = SECTION_ICONS[section.key] || Inbox;
                return (
                  <span key={section.key} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${theme.chip}`} data-testid={`summary-chip-${section.key}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {theme.label} · <span className="font-bold">{count}</span>
                  </span>
                );
              })}
              {pending?.total_pending > 0 && (
                <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-red-500 text-white" data-testid="summary-total">
                  {pending.total_pending} pending
                </span>
              )}
            </div>
          )}

          {(pending?.sections || []).map((section) => {
            const Icon = SECTION_ICONS[section.key] || Inbox;
            const theme = SECTION_THEME[section.key] || {};
            const hasItems = (section.items || []).length > 0;
            return (
              <Card key={section.key} data-testid={`section-${section.key}`} className={hasItems ? 'border-l-4 border-l-red-500' : ''}>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Icon className={`w-4 h-4 ${theme.icon || ''}`} />
                    {section.label}
                    <Badge variant={hasItems ? 'destructive' : 'outline'} className="ml-1">{section.items.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {section.items.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-3 text-center">Nothing waiting on your action.</p>
                  ) : (
                    <div className="space-y-2">
                      {section.items.map((item) => renderItem(section, item))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="recent" className="mt-4 space-y-3">
          {(recent?.sections || []).map((section) => (
            <Card key={section.key}>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  {section.label}
                  <Badge variant="outline" className="ml-1">{section.items.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {section.items.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-3 text-center">No recent decisions.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="data-table w-full">
                      <thead><tr><th>When</th><th>Employee</th><th>Status</th><th>Details</th></tr></thead>
                      <tbody>
                        {section.items.map((item) => (
                          <tr key={item.id}>
                            <td className="text-xs">{(item.updated_at || item.created_at || '').slice(0, 10)}</td>
                            <td className="text-sm">{item.employee_name || (item.employee_id || '').slice(0, 8)}</td>
                            <td>
                              <Badge variant="outline" className={
                                item.status === 'approved' ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300'
                                : item.status === 'rejected' ? 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/30 dark:text-rose-300'
                                : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300'
                              }>{(item.status || '').toUpperCase()}</Badge>
                            </td>
                            <td className="text-xs text-muted-foreground">{item.summary || item.purpose || item.category || item.letter_type || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!actingOn} onOpenChange={(o) => { if (!o) { setActingOn(null); setActionType(null); setRemarks(''); } }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className={actionType === 'approve' ? 'text-emerald-700' : 'text-rose-700'}>
              {actionType === 'approve' ? 'Approve' : 'Reject'} {actingOn?.section.label.toLowerCase()}
            </DialogTitle>
            <DialogDescription>Optional remarks will be emailed to the requester.</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Reason / remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} className="text-sm" rows={3} data-testid="approval-remarks" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setActingOn(null)}>Cancel</Button>
            <Button onClick={performAction} disabled={submitting}
              className={actionType === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-rose-600 hover:bg-rose-700 text-white'}
              data-testid="approval-confirm">
              {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
              Confirm {actionType === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsPage;
